"use client";

import { useState, useMemo } from "react";
import { Search, Library, X, ExternalLink, FileText, Download } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { ResourceItem } from "@/types";

interface ResourcesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  resources: ResourceItem[];
}

export function ResourcesModal({ open, onOpenChange, title, resources }: ResourcesModalProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Filter resources
  const filteredResources = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    
    if (!query) {
      return resources;
    }
    
    return resources.filter(
      (resource) =>
        resource.title.toLowerCase().includes(query) ||
        (resource.description?.toLowerCase().includes(query) ?? false)
    );
  }, [resources, searchQuery]);

  // Reset search when modal closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchQuery("");
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Library className="h-5 w-5" />
            {title || "Ressources"}
          </DialogTitle>
        </DialogHeader>
        
        {/* Search Input */}
        {resources.length > 3 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher une ressource..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        
        {/* Resources List */}
        <ScrollArea className="flex-1 -mx-6 px-6">
          {filteredResources.length === 0 ? (
            <div className="text-center py-12">
              <Library className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              {searchQuery ? (
                <>
                  <p className="text-muted-foreground">
                    Aucune ressource trouvée pour « {searchQuery} »
                  </p>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    Essayez avec d&apos;autres mots-clés
                  </p>
                </>
              ) : (
                <p className="text-muted-foreground">
                  Aucune ressource disponible
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 pb-4">
              {filteredResources.map((resource) => (
                <a
                  key={resource.id}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={resource.type === "file" ? resource.file_name || true : undefined}
                  className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors group"
                >
                  <div className={`h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    resource.type === "link" ? "bg-info/10" : "bg-warning/10"
                  }`}>
                    {resource.type === "link" ? (
                      <ExternalLink className="h-6 w-6 text-info" />
                    ) : (
                      <FileText className="h-6 w-6 text-warning" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium group-hover:text-primary transition-colors">
                      {highlightMatch(resource.title, searchQuery)}
                    </p>
                    {resource.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {highlightMatch(resource.description, searchQuery)}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground/70">
                      {resource.type === "link" ? (
                        <>
                          <ExternalLink className="h-3 w-3" />
                          <span>Ouvre dans un nouvel onglet</span>
                        </>
                      ) : (
                        <>
                          <Download className="h-3 w-3" />
                          <span>Télécharger{resource.file_name ? ` (${resource.file_name})` : ""}</span>
                        </>
                      )}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {/* Footer with count */}
        {filteredResources.length > 0 && (
          <div className="text-xs text-muted-foreground text-center pt-2 border-t">
            {searchQuery
              ? `${filteredResources.length} ressource${filteredResources.length > 1 ? "s" : ""} trouvée${filteredResources.length > 1 ? "s" : ""}`
              : `${resources.length} ressource${resources.length > 1 ? "s" : ""} disponible${resources.length > 1 ? "s" : ""}`}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Helper function to highlight search matches
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query.trim()) {
    return text;
  }
  
  const regex = new RegExp(`(${escapeRegExp(query)})`, "gi");
  const parts = text.split(regex);
  
  return parts.map((part, index) =>
    regex.test(part) ? (
      <mark key={index} className="bg-yellow-200 dark:bg-yellow-900 rounded px-0.5">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

// Helper to escape special regex characters
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
